import React, {KeyboardEvent, useCallback, useEffect, useRef, useState} from "react";
import {useErrorReporter} from "../general/ErrorViewer";
import {useSupportCase} from "../support/CaseHook";
import {AttachIcon, SendIcon} from "../components/Icons";
import {ActionDropdown, ActionEntry} from "./ActionDropdown";

interface CaseInput2Props {
    disabled: boolean;
    actionConfig: ActionEntry[];
    forceEnabled?: boolean;
    className?: string;
    isUploading?: boolean;
    progressPercent?: number;
}

export function CaseInput2({
    disabled,
    actionConfig,
    forceEnabled,
    className,
    isUploading = false,
    progressPercent = 0,
}: CaseInput2Props) {
    const [text, setText] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [activeActionId, setActiveActionId] = useState(actionConfig[0]?.id ?? "");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const reportError = useErrorReporter();
    const {webSocketEnsureConnected} = useSupportCase();
    const configuredActiveAction = actionConfig.find((action) => action.id === activeActionId);
    const activeAction = configuredActiveAction ?? actionConfig[0];

    useEffect(() => {
        if (configuredActiveAction == null && actionConfig[0] != null) {
            setActiveActionId(actionConfig[0].id);
        }
    }, [actionConfig, configuredActiveAction]);

    useEffect(() => {
        if (activeAction?.allowsAttachment === false) {
            setSelectedFile(null);
        }
    }, [activeAction?.allowsAttachment]);

    const handleInputSubmit = useCallback(async () => {
        if (activeAction == null) return;

        const submittedText = text;
        const submittedFile = selectedFile;
        setText("");
        setSelectedFile(null);
        webSocketEnsureConnected();
        const success = await activeAction.submit(submittedText, submittedFile ?? undefined);
        if (success) {
            setActiveActionId(actionConfig[0]?.id ?? "");
        } else {
            setText(submittedText);
            setSelectedFile(submittedFile);
        }
    }, [actionConfig, activeAction, selectedFile, text, webSocketEnsureConnected]);

    const submitOnCommandEnter = useCallback(async (event: KeyboardEvent<HTMLTextAreaElement>) => {
        const isMac = navigator.userAgent.toLowerCase().includes("mac os");
        if (
            (!isMac && event.getModifierState("Control") && event.key === "Enter") ||
            (isMac && event.getModifierState("Meta") && event.key === "Enter")
        ) {
            if (!event.repeat) await handleInputSubmit();
            event.preventDefault();
        }
    }, [handleInputSubmit]);

    const handleFileListChange = useCallback(async (files: FileList | null) => {
        if (files == null || files.length === 0) return;
        const file = files[0];
        if (file.size > 400 * 1024) {
            reportError("File too large. Maximum size is 400kB");
            return;
        }
        const fileType = file.name.endsWith(".log") ? "text/plain" : file.type;
        if (fileType !== "text/plain") {
            reportError(`Invalid file type ${fileType}. Only text files are allowed.`);
            return;
        }
        setSelectedFile(file);
    }, [reportError]);

    const isFileUploadDisabled = disabled || activeAction?.allowsAttachment !== true;
    const missingInput = activeAction.requiresMessage && text.length === 0 && forceEnabled !== true;
    const isSubmitDisabled = disabled || activeAction == null || missingInput;
    const handleFlashTextAreaBorder = useCallback(async () => {
        console.log("flashing textarea border")
        if (textareaRef.current == null) return;
        // textareaRef.current.classList.add("border-1")
        textareaRef.current.classList.add("case-input-textarea-border-flash")
    }, [])

    const removeAnimation = useCallback(async () => {
        if (textareaRef.current == null) return;
        textareaRef.current.classList.remove("case-input-textarea-border-flash")
    }, [])

    return (
        <div className={`w-full flex flex-col items-center ${className ?? ""} `}>
            <div className="w-full flex justify-center px-4 py-4 bg-transparent max-w-[850px] ">
                <div className="flex flex-col w-full rounded-2xl border border-gray-600 bg-gray-really focus-within:border-gray-400 px-4 py-3">
                    <textarea
                        ref={textareaRef}
                        id="chat-input-textarea-id"
                        autoFocus
                        className="min-h-[24px] max-h-[30dvh] resize-none border-0 bg-transparent text-sm text-white outline-none placeholder:text-gray-400 focus:ring-0 overflow-y-auto [field-sizing:content] py-3 px-0"
                        placeholder="Type a new question ..."
                        disabled={disabled}
                        rows={1}
                        maxLength={20000}
                        onChange={(event) => {
                            webSocketEnsureConnected();
                            setText(event.target.value);
                        }}
                        onKeyDown={submitOnCommandEnter}
                        onAnimationEnd={removeAnimation}
                        value={text}
                    />
                    <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center justify-center">
                            <div className="flex items-center gap-2">
                                <label
                                    htmlFor="case-input-attachment-input-id"
                                    className={`cursor-pointer ${selectedFile == null ? "text-gray-400 hover:text-white" : "text-blue-enov"}`}
                                    data-testid="case-input-attachement-label"
                                >
                                    <AttachIcon/>
                                    <input
                                        id="case-input-attachment-input-id"
                                        type="file"
                                        className="hidden"
                                        disabled={isFileUploadDisabled}
                                        onChange={(event) => handleFileListChange(event.target.files)}
                                        data-testid="case-input-attachment"
                                    />
                                </label>
                                {isUploading && <progress value={progressPercent} max="100" className="w-20 h-1" data-testid="progress"/>}
                                {selectedFile != null && <div className="text-blue-enov text-xs text-opacity-20" data-testid="filesdiv">{selectedFile.name}</div>}
                            </div>
                            <ActionDropdown
                                disabled={disabled}
                                actionConfig={actionConfig}
                                activeActionId={activeAction?.id ?? ""}
                                onActionChange={setActiveActionId}
                            />
                        </div>
                        <button
                            id="case-input-send-button-id"
                            type="button"
                            aria-label={activeAction?.label}
                            className="flex items-center px-2 py-1 rounded-lg text-white hover:bg-gray-700 transition-colors"
                            disabled={disabled}
                            onClick={missingInput ? handleFlashTextAreaBorder : handleInputSubmit}
                        >
                            <SendIcon/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
